import { useMemo } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useUserSettingsList } from '../api';
import type { UserSettingRow } from '../types';

/* Display helpers — margin "1..10" → "10%..100%"; intervals → "Ns". */
const formatMargin = (raw: string | undefined): string =>
  raw ? `${Number(raw) * 10}%` : '—';
const formatInterval = (raw: string | undefined): string =>
  raw ? `${raw}s` : '—';
const formatThreshold = (raw: string | undefined): string => raw || '—';

function TierBadge({ tier }: { tier: '1' | '2' }) {
  return tier === '2' ? (
    <Badge variant="success">Enabled</Badge>
  ) : (
    <Badge variant="outline">Off</Badge>
  );
}

export function UserSettingsListPage() {
  const navigate = useNavigate();
  const canView = usePermission('Manage Users.Tier Pricing', BASE_PERMISSION.VIEW);
  const query = useUserSettingsList();
  const rows = query.data ?? [];

  const columns = useMemo<ColumnDef<UserSettingRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.userId}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        cell: ({ row }) => (
          <Link
            to={editPath(row.original)}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.username}
          </Link>
        ),
      },
      /* Tier 1 columns */
      {
        accessorKey: 'tier1',
        header: 'Tier 1',
        size: 90,
        cell: ({ row }) => <TierBadge tier={row.original.tier1} />,
      },
      {
        accessorKey: 't1_margin',
        header: 'T1 margin',
        size: 90,
        cell: ({ row }) => formatMargin(row.original.t1_margin),
      },
      {
        accessorKey: 't1_interval',
        header: 'T1 interval',
        size: 100,
        cell: ({ row }) => formatInterval(row.original.t1_interval),
      },
      {
        accessorKey: 't1_threshold',
        header: 'T1 threshold',
        size: 110,
        cell: ({ row }) => formatThreshold(row.original.t1_threshold),
      },
      /* Tier 2 columns */
      {
        accessorKey: 'tier2',
        header: 'Tier 2',
        size: 90,
        cell: ({ row }) => <TierBadge tier={row.original.tier2} />,
      },
      {
        accessorKey: 't2_margin',
        header: 'T2 margin',
        size: 90,
        cell: ({ row }) => formatMargin(row.original.t2_margin),
      },
      {
        accessorKey: 't2_start_interval',
        header: 'T2 start',
        size: 90,
        cell: ({ row }) => formatInterval(row.original.t2_start_interval),
      },
      {
        accessorKey: 't2_end_interval',
        header: 'T2 end',
        size: 90,
        cell: ({ row }) => formatInterval(row.original.t2_end_interval),
      },
      {
        accessorKey: 't2_threshold',
        header: 'T2 threshold',
        size: 110,
        cell: ({ row }) => formatThreshold(row.original.t2_threshold),
      },
      /* Tier 3 columns */
      {
        accessorKey: 'tier3',
        header: 'Tier 3',
        size: 90,
        cell: ({ row }) => <TierBadge tier={row.original.tier3} />,
      },
      {
        accessorKey: 't3_margin',
        header: 'T3 margin',
        size: 90,
        cell: ({ row }) => formatMargin(row.original.t3_margin),
      },
      {
        accessorKey: 't3_start_interval',
        header: 'T3 start',
        size: 90,
        cell: ({ row }) => formatInterval(row.original.t3_start_interval),
      },
      {
        accessorKey: 't3_end_interval',
        header: 'T3 end',
        size: 90,
        cell: ({ row }) => formatInterval(row.original.t3_end_interval),
      },
      {
        accessorKey: 't3_threshold',
        header: 'T3 threshold',
        size: 110,
        cell: ({ row }) => formatThreshold(row.original.t3_threshold),
      },
      /* Tier 4 columns */
      {
        accessorKey: 'tier4',
        header: 'Tier 4',
        size: 90,
        cell: ({ row }) => <TierBadge tier={row.original.tier4} />,
      },
      {
        accessorKey: 't4_margin',
        header: 'T4 margin',
        size: 90,
        cell: ({ row }) => formatMargin(row.original.t4_margin),
      },
      {
        accessorKey: 't4_interval',
        header: 'T4 interval',
        size: 100,
        cell: ({ row }) => formatInterval(row.original.t4_interval),
      },
      {
        accessorKey: 't4_threshold',
        header: 'T4 threshold',
        size: 110,
        cell: ({ row }) => formatThreshold(row.original.t4_threshold),
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(editPath(row.original))}
            title={`Edit settings for ${row.original.username}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [navigate],
  );

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Tier Pricing"
        description="Tier-based pricing configuration per user. Each tier scopes a billing margin to a call-duration window."
      />
      <DataTable<UserSettingRow, unknown>
        tableId="users:settings:v1"
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search users…"
        emptyTitle="No settings configured"
        emptyDescription="Users with custom tier pricing will appear here."
        getRowId={(row) => `${row.subId}:${row.userId}`}
        exportFilename="user-settings.csv"
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
      />
    </FillPage>
  );
}

function editPath(row: UserSettingRow): string {
  return `/user-settings/edit/${row.subId || 0}/${row.userId}`;
}
